import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import api from 'state/axios';
import {
  Box, 
  Typography, 
  Avatar, 
  List, 
  ListItem, 
  ListItemAvatar,
  ListItemText, 
  TextField, 
  Divider, 
  IconButton, 
  Chip,
  Tooltip, 
} from '@mui/material';
import { 
  Add, 
  DeleteOutline, 
  Assistant 
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { formatDistanceToNow } from 'date-fns';
import {
  setConversations, 
  addOrUpdateConversation, 
  removeConversation, 
  setUnreadCount,
} from 'state';
import { getSocket } from 'state/socket';
import NewConversationDialog from './NewConversationDialog';


const OnlineDot = ({ isOnline }) => (
  <Box
    sx={{
      width: 9, height: 9, borderRadius: '50%',
      backgroundColor: isOnline ? '#44b700' : 'grey.400',
      border: '1.5px solid white',
      position: 'absolute', bottom: 1, right: 1,
    }}
  />
);

const ChatConversationList = ({ onSelect, selectedId, onlineUsers = [], onClose }) => {
  const dispatch = useDispatch();
  const currentUserId = useSelector((state) => state.user?._id);
  const conversations = useSelector((state) => state.conversations);
  const theme = useTheme();

  const [search, setSearch] = useState('');
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  // const [aiLoading, setAILoading] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);


  /* Fetch conversations + unread count on mount */
  useEffect(() => {
    api.get('/conversations').then((res) => {
      dispatch(setConversations(res.data));
    });
    api.get('/conversations/unread').then((res) => {
      dispatch(setUnreadCount(res.data.count));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Socket: handle incoming notifications (user not in chat room) */
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleNotify = ({ conversationId, message }) => {
      dispatch(addOrUpdateConversation({
        ...conversations.find((c) => c._id === conversationId),
        _id: conversationId,
        lastMessage: message,
        lastMessageAt: message.createdAt,
      }));
    };

    socket.on('chat:notify', handleNotify);
    return () => socket.off('chat:notify', handleNotify);
  }, [conversations, dispatch]);

  // const handleOpenAIChat = async () => {
  //   try {
  //     setAILoading(true);
  //     const res = await api.post('/conversations/ai', {});
  //     dispatch(addOrUpdateConversation(res.data));
  //     onSelect(res.data);
  //   } catch {
  //     // error handled silently
  //   } finally {
  //     setAILoading(false);
  //   }
  // };

  const getName = (conv) => {
    if (conv.type === 'ai') return 'DigitAir AI';
    if (conv.type === 'group') return conv.name;
    const other = conv.participants?.find((p) => String(p._id) !== String(currentUserId));
    return other ? `${other.firstName} ${other.lastName}` : 'Chat';
  };

  const getAvatar = (conv) => {
    if (conv.type === 'group') return conv.avatar || undefined;
    const other = conv.participants?.find((p) => String(p._id) !== String(currentUserId));
    return other?.picturePath || undefined;
  };

  const isOnline = (conv) => {
    if (conv.type === 'group') return false;
    const other = conv.participants?.find((p) => String(p._id) !== String(currentUserId));
    return other ? onlineUsers.includes(String(other._id)) : false;
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return conversations;
    const q = search.toLowerCase();
    return conversations.filter((c) => getName(c).toLowerCase().includes(q));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, search]);

  const handleHide = async (e, convId) => {
    e.stopPropagation();
    try {
      await api.delete(`/conversations/${convId}`);
      dispatch(removeConversation(convId));
    } catch {
      // silently ignore
    }
  };

  const handleCreated = (conv) => {
    dispatch(addOrUpdateConversation(conv));
    onSelect(conv);
  };

  return (
    <Box 
      display="flex" 
      flexDirection="column" 
      height="100%" 
      sx={{ borderRight: '1px solid', borderColor: 'divider' }}
    >
      {/* Header */}
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="space-between" 
        sx={{ px: 1.5, py: 1, flexShrink: 0 }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          Messages
        </Typography>
        <Box display="flex" alignItems="center">
          {/* <Tooltip title="Chat with AI">
            <IconButton size="medium" onClick={handleOpenAIChat} disabled={aiLoading} color="primary">
              <Assistant fontSize="small" />
            </IconButton>
          </Tooltip> */}
          <Tooltip title="New conversation">
            <IconButton size="medium" onClick={() => setNewDialogOpen(true)}>
              <Add fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* {onClose && (
            <Tooltip title="Close">
              <IconButton size="medium" onClick={onClose}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          )} */}
        </Box>
      </Box>

      <Box sx={{ px: 1.5, pb: 0.75, flexShrink: 0 }}>
        <TextField
          fullWidth size="small" variant="outlined"
          placeholder="Search …"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
        />
      </Box>

      <Divider />

      {/* List */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <Typography 
            variant="caption" 
            color="text.disabled" 
            sx={{ p: 2, display: 'block', textAlign: 'center' }}
          >
            No conversations yet
          </Typography>
        ) : (
          <List dense disablePadding>
            {filtered.map((conv) => {
              const name = getName(conv);
              const initials = name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();
              const avatarSrc = getAvatar(conv);
              const online = isOnline(conv);
              const isSelected = conv._id === selectedId;
              const preview = conv.lastMessage?.content || (conv.lastMessage?.attachments?.length ? '📎 Attachment' : '');
              const timeAgo = conv.lastMessageAt
                ? formatDistanceToNow(new Date(conv.lastMessageAt), { addSuffix: true })
                : '';

              return (
                <ListItem
                  key={conv._id}
                  button
                  selected={isSelected}
                  onClick={() => onSelect(conv)}
                  onMouseEnter={() => setHoveredId(conv._id)}
                  onMouseLeave={() => setHoveredId(null)}
                  sx={{
                    px: 1.5,
                    borderRadius: 1,
                    mb: 0.25,
                    backgroundColor: isSelected
                      ? theme.palette.action.selected
                      : 'transparent',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Box sx={{ position: 'relative', width: 36, height: 36 }}>
                      {conv.type === 'ai' ? (
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                          <Assistant fontSize="small" />
                        </Avatar>
                      ) : (
                        <Avatar
                          src={avatarSrc}
                          sx={{ width: 36, height: 36, fontSize: '0.8rem' }}
                        >
                          {initials}
                        </Avatar>
                      )}
                      {conv.type === 'direct' && <OnlineDot isOnline={online} />}
                    </Box>
                  </ListItemAvatar>
                  <ListItemText
                    sx={{ overflow: 'hidden', minWidth: 0 }}
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        noWrap
                      >
                        {name}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        {preview}
                      </Typography>
                    }
                  />
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                    gap={0.25}
                    sx={{ ml: 0.5, flexShrink: 0 }}
                  >
                    {hoveredId === conv._id ? (
                      <Tooltip title="Remove from list">
                        <IconButton
                          size="small"
                          onClick={(e) => handleHide(e, conv._id)}
                          sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                        >
                          <DeleteOutline sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <>
                        {timeAgo && (
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}
                          >
                            {timeAgo}
                          </Typography>
                        )}
                        {conv.type === 'group' && (
                          <Chip label="Group" size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                        )}
                        {conv.type === 'ai' && (
                          <Chip label="AI" size="small" color="primary" sx={{ height: 16, fontSize: '0.6rem' }} />
                        )}
                      </>
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      <NewConversationDialog
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        onConversationCreated={handleCreated}
      />
    </Box>
  );
};

export default ChatConversationList;
